import { BrandLogo } from '@seek/branding-assets';
import { useTranslations } from '@vocab/react';
import {
  Box,
  ButtonIcon,
  ButtonLink,
  Column,
  Columns,
  Dialog,
  Heading,
  IconInfo,
  Inline,
  Rating,
  Stack,
  Text,
  TextLink,
  TextLinkButton,
} from 'braid-design-system';
import { useState } from 'react';
import { DotIcon } from './DotIcon';

import {
  buildClaimCompanyProfileURI,
  buildEmployerCompanyProfilePromoPageURI,
  useConfig,
  type CompanyProfile,
} from '../../../';

import { createImageObject } from '../../../utils/imageUtils';
import translations from './.vocab';

export type CompanyBrandingSectionModel = Pick<
  CompanyProfile,
  'branding' | 'name' | 'rating' | 'id' | 'zone' | 'isClaimed'
>;

interface Props {
  aboutPageLink: string;
  navigateToAboutTab: () => void;
  navigateToReviewsTab: () => void;
  trackWriteAReviewClicked: () => void;
  model?: Omit<CompanyBrandingSectionModel, 'id'>;
}

export function CompanyProfileSummary({
  model,
  navigateToReviewsTab,
  navigateToAboutTab,
  aboutPageLink,
}: Props) {
  const { t } = useTranslations(translations);
  const { country, language, environment } = useConfig();
  const [showDialog, setShowDialog] = useState(false);

  const logoImageAsset = createImageObject(
    'jdpLogo',
    model?.branding?.logoImageUrl,
  );

  const onEmployerClick = () => {
    setShowDialog(true);
  };

  const onDialogClose = () => {
    setShowDialog(false);
  };

  const totalRating = model?.rating?.average || 0;
  const numberOfReviews = model?.rating?.count;

  return (
    <Columns collapseBelow="tablet" space="large" align="left" alignY="center">
      <Column>
        <Box
          display={{ mobile: 'inlineBlock', tablet: 'flex' }}
          flexDirection={{ mobile: 'column' }}
        >
          <Box
            paddingBottom={{
              mobile: 'medium',
            }}
          >
            <BrandLogo logo={logoImageAsset} />
          </Box>
          <Box
            display="flex"
            flexDirection={{ mobile: 'row', tablet: 'column' }}
            alignItems="flexStart"
            justifyContent={{ mobile: 'flexStart', tablet: 'center' }}
          >
            {model?.name ? (
              <TextLink
                href={aboutPageLink}
                onClick={(e) => {
                  e.preventDefault();
                  navigateToAboutTab();
                }}
              >
                <Heading level="4" component="span">
                  {model.name}
                </Heading>
              </TextLink>
            ) : null}
            {model?.rating ? (
              <Box
                paddingTop={{
                  mobile: 'none',
                  tablet: 'medium',
                }}
                paddingLeft={{
                  mobile: 'small',
                  tablet: 'none',
                }}
              >
                <Inline space="xxsmall" alignY="center">
                  <Rating rating={totalRating} variant="minimal" />
                  <Text>
                    <TextLinkButton
                      onClick={navigateToReviewsTab}
                      weight="weak"
                    >
                      ({numberOfReviews} {t('reviews')})
                    </TextLinkButton>
                  </Text>
                  {model?.isClaimed === false && (
                    <>
                      <Box display="flex" alignItems="center">
                        <DotIcon />
                      </Box>
                      <Text>{t('Unclaimed')}</Text>
                      <ButtonIcon
                        size="small"
                        icon={<IconInfo />}
                        label={t('Learn more')}
                        onClick={onEmployerClick}
                        variant="transparent"
                      />
                    </>
                  )}
                </Inline>

                <Dialog title="" open={showDialog} onClose={onDialogClose}>
                  <Stack space="gutter">
                    <Heading level="3">
                      {t('Are you an employer of this company?')}
                    </Heading>
                    <Text>
                      {t(
                        "The information on this page hasn't been verified or updated by {name}. If you're an employer of this company, you can take ownership of the profile to update content and respond to reviews.",
                        { name: model?.name },
                      )}
                    </Text>
                    <Inline space="small" align="right">
                      <ButtonLink
                        href={buildEmployerCompanyProfilePromoPageURI({
                          country,
                          environment,
                          language,
                        })}
                        target="_blank"
                        variant="transparent"
                        tone="formAccent"
                      >
                        {t('Learn more')}
                      </ButtonLink>
                      <ButtonLink
                        href={buildClaimCompanyProfileURI({
                          country,
                          environment,
                          language,
                        })}
                        target="_blank"
                        variant="solid"
                        tone="formAccent"
                      >
                        {t('Claim Profile')}
                      </ButtonLink>
                    </Inline>
                  </Stack>
                </Dialog>
              </Box>
            ) : null}
          </Box>
        </Box>
      </Column>
    </Columns>
  );
}
